import { assert, error, log } from '../app/logger.js';
import { HTMLControl } from '../controls/html_control.js';

/**
 * Serves to get/set data from a checklist view (either checklsit editor of gear pack view).
 */
class ChecklistViewBase extends HTMLControl {
  set(checklist) {
    if (checklist == this.checklist) {
      return this.sync();
    }

    if (this.pipe) {
      this.pipe.unpipe();
    }

    this.checklist = checklist;

    this.loadingOn(); // #refreshUI will off loading.
    this.clear();
    this.pipeToData((tree, diff) => this.#refreshUI(tree, diff));
  }

  sync() {
    if (!this.checklist) {
      error(`[checklist] no checklist set`);
      return null;
    }
    log(`[checklist] sync ${this.checklist.id} checklist`);
    return this.checklist.loadIfNeeded();
  }

  clear() {
    this.innerHTML = '';
  }

  getDataFromUI() {
    return this.#getDataFromUI();
  }

  checklistChanged() {
    this.ownerDocument.dispatchEvent(new Event('checklist-change'));
  }

  #refreshUI(dataItems, diff) {
    let uiItems = this.#getDataFromUI();
    for (let entry of diff) {
      let entryItem = entry.origData;
      if (!entryItem) {
        error(`[checklist] unexpected diff item ${JSON.stringify(entry)}`);
        continue;
      }
      switch (entry.what) {
        case 'changed': {
          let uiData = this.#findUIItem(uiItems, entryItem);
          if (uiData) {
            this.setItem(uiData.el, entry.newData);
          }
          break;
        }

        case 'deleted': {
          let uiData = this.#findUIItem(uiItems, entryItem);
          if (uiData) {
            let subUl =
              uiData.el.nextElementSibling &&
              uiData.el.nextElementSibling.localName == 'ul' &&
              uiData.el.nextElementSibling;
            let ul = uiData.el.parentNode;
            ul.removeChild(uiData.el);

            // If items are removed from top to bottom, then make sure to
            // remove all empty UL in parent chain.
            ul = subUl || ul;
            while (ul && ul.localName == 'ul' && ul.childElementCount == 0) {
              let parent = ul.parentNode;
              parent.removeChild(ul);
              ul = parent;
            }
          }
          break;
        }

        case 'inserted': {
          let uiData = this.#findUIItem(uiItems, entryItem.parent);
          if (uiData != uiItems) {
            let li = uiData.el;
            let nestedUl = li.nextElementSibling;
            if (nestedUl && nestedUl.localName == 'ul') {
              this.#insertItem(nestedUl, entryItem);
            } else {
              this.#insertList(
                [entryItem],
                li.parentNode,
                li.nextElementSibling
              );
            }
          } else {
            let el = this.firstElementChild;
            for (
              let idx = 0;
              el && idx < entry.idx;
              el = el.nextElementSibling
            ) {
              if (el.localName == 'ul') {
                idx++;
              }
            }
            this.#insertList([entryItem], this, el);
          }
          break;
        }

        default:
          break;
      }
    }

    this.loadingOff();
  }

  /**
   * Returns a new 'ChecklistItemsData' instance for items in UI.
   */
  #getDataFromUI() {
    let rootItemData = { children: [] };

    // Traverse top level items.
    for (
      let ulEl = this.firstElementChild;
      ulEl;
      ulEl = ulEl.nextElementSibling
    ) {
      if (ulEl.localName != 'ul') {
        error(
          `[checklist] only UL elements are allowed in editor, got: ${ulEl.innerHTML}`
        );
        continue;
      }
      this.#getDataFromUIReq(ulEl, rootItemData);
    }

    return rootItemData;
  }

  #getDataFromUIReq(ulEl, parentItemData) {
    let itemData = null;
    for (let el = ulEl.firstElementChild; el; el = el.nextElementSibling) {
      switch (el.localName) {
        case 'li':
          {
            itemData = {
              name: el.textContent,
              children: [],
              el,
              parent: parentItemData,
            };
            let checkboxEl = el.querySelector('input');
            if (checkboxEl) {
              itemData.checked = checkboxEl.checked;
            }
            parentItemData.children.push(itemData);
          }
          break;

        case 'ul':
          if (
            el.previousElementSibling &&
            el.previousElementSibling.localName == 'li'
          ) {
            this.#getDataFromUIReq(el, itemData);
          } else {
            error(`[checklist] UL has to follow its LI, UL: ${el.innerHTML}`);
          }
          break;

        default:
          error(
            `[checklist] unexpected tag element in the list, got ${el.innerHTML}`
          );
      }
    }
  }

  #findUIItem(uiItem, itemData) {
    assert(uiItem, `[checklist] #findUIItem: no uiItem`);
    assert(itemData, `[checklist] #findUIItem: no itemData`);

    let indexesChain = [];
    for (
      let dataChainItem = itemData;
      dataChainItem.parent;
      dataChainItem = dataChainItem.parent
    ) {
      indexesChain.push(dataChainItem.parent.children.indexOf(dataChainItem));
    }

    let curUIItem = uiItem;
    for (let indexInParent of indexesChain.reverse()) {
      curUIItem = curUIItem.children[indexInParent];
    }

    return curUIItem;
  }

  #insertList(items, containerEl, siblingEl) {
    let ul = this.ownerDocument.createElement('ul');
    for (let item of items) {
      let li = ul.appendChild(this.ownerDocument.createElement('li'));
      this.setItem(li, item);
      if (item.children && item.children.length > 0) {
        this.#insertList(item.children, ul);
      }
    }
    containerEl.insertBefore(ul, siblingEl);
  }

  #insertItem(containerEl, item) {
    let idx = 0;
    let el = null;
    for (
      el = containerEl.firstElementChild;
      el && idx < item.idx;
      el = el.nextElementSibling
    ) {
      if (el.localName == 'li') {
        idx++;
      }
    }

    let li = this.ownerDocument.createElement('li');
    this.setItem(li, item);
    containerEl.insertBefore(li, el);
    item.el = li;

    if (item.children && item.children.length > 0) {
      this.#insertList(item.children, containerEl, li.nextElementSibling);
    }
  }

  setItem() {
    error(`[checklist] ChecklistViewBase::setItem must be overloaded`);
  }

  static uiname = 'ChecklistViewBase';

  checklist;
  pipe;
}

export { ChecklistViewBase };
